// client/src/components/pages/ProductsPage.js
import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import CategorySidebar from '../products/CategorySidebar';
import ProductCard from '../products/ProductCard';
import Pagination from '../products/Pagination';
import { useInView } from 'react-intersection-observer';
import './ProductsPage.css';

const fromUrlSlug = (slug) => {
  if (!slug) return '';
  return slug.replace(/-/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
};

const ProductsPage = () => {
  const [allProducts, setAllProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('name-asc');

  const { categorySlug } = useParams();
  const { pathname } = useLocation();
  const [isVisible, setIsVisible] = useState(false);
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [productsPerPage] = useState(15);

  useEffect(() => {
    if (inView) setIsVisible(true);
  }, [inView]);

  useEffect(() => {
    setIsVisible(false);
    const timer = setTimeout(() => setIsVisible(true), 50);
    return () => clearTimeout(timer);
  }, [pathname]);

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/products`);
        setAllProducts(response.data);
      } catch (err) {
        setError('Could not load products. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  // Reset pagination when category or search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [categorySlug, searchQuery, sortBy]);

  const activeCategory = categorySlug ? fromUrlSlug(categorySlug) : null;
  const pageTitle = activeCategory ? activeCategory : 'All Products';

  // Filter by category, search query, and sort order
  const filteredProducts = useMemo(() => {
    const byCategory = activeCategory
      ? allProducts.filter((p) => p.category === activeCategory)
      : allProducts;

    const query = searchQuery.trim().toLowerCase();
    const bySearch = query
      ? byCategory.filter(
          (p) =>
            (p.name && p.name.toLowerCase().includes(query)) ||
            (p.category && p.category.toLowerCase().includes(query)) ||
            (p.description && p.description.toLowerCase().includes(query))
        )
      : byCategory;

    const sorted = [...bySearch];
    if (sortBy === 'name-asc') {
      sorted.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    } else if (sortBy === 'name-desc') {
      sorted.sort((a, b) => (b.name || '').localeCompare(a.name || ''));
    } else if (sortBy === 'category') {
      sorted.sort((a, b) => (a.category || '').localeCompare(b.category || ''));
    }
    return sorted;
  }, [allProducts, activeCategory, searchQuery, sortBy]);

  const indexOfLastProduct = currentPage * productsPerPage;
  const indexOfFirstProduct = indexOfLastProduct - productsPerPage;
  const currentProducts = filteredProducts.slice(indexOfFirstProduct, indexOfLastProduct);

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 180, behavior: 'smooth' });
  };

  return (
    <div ref={ref} className={`products-page-container ${isVisible ? 'is-visible' : ''}`}>
      <header className="products-page-header">
        <h1>{pageTitle}</h1>
        <p>Explore our extensive catalog of high-quality medical equipment and supplies.</p>
      </header>

      <div className="products-page-layout">
        <CategorySidebar activeCategory={activeCategory} />

        <div className="products-main-content">
          {/* --- Instant Search & Filter Toolbar --- */}
          <div className="products-toolbar">
            <div className="products-search-box">
              <i className="fas fa-search search-icon" aria-hidden="true"></i>
              <input
                type="search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={`Search ${activeCategory ? activeCategory.toLowerCase() : 'all medical equipment & supplies'}...`}
                aria-label="Search products"
              />
              {searchQuery && (
                <button
                  type="button"
                  className="clear-search-btn"
                  onClick={() => setSearchQuery('')}
                  aria-label="Clear search"
                >
                  <i className="fas fa-times"></i>
                </button>
              )}
            </div>

            <div className="products-toolbar-meta">
              {!loading && (
                <span className="products-count-pill">
                  Showing <strong>{filteredProducts.length}</strong>{' '}
                  {filteredProducts.length === 1 ? 'item' : 'items'}
                </span>
              )}

              <div className="products-sort-wrapper">
                <label htmlFor="product-sort">Sort:</label>
                <select
                  id="product-sort"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                >
                  <option value="name-asc">Name (A – Z)</option>
                  <option value="name-desc">Name (Z – A)</option>
                  <option value="category">Category</option>
                </select>
              </div>
            </div>
          </div>

          <main className="products-grid">
            {/* --- Shimmer Skeleton Cards While Loading --- */}
            {loading &&
              Array.from({ length: 6 }).map((_, idx) => (
                <div key={`skeleton-${idx}`} className="product-skeleton-card" aria-hidden="true">
                  <div className="skeleton-image shimmer"></div>
                  <div className="skeleton-body">
                    <div className="skeleton-line skeleton-tag shimmer"></div>
                    <div className="skeleton-line skeleton-title shimmer"></div>
                    <div className="skeleton-line skeleton-cta shimmer"></div>
                  </div>
                </div>
              ))}

            {error && <p className="error-message">{error}</p>}

            {!loading && !error && filteredProducts.length === 0 && (
              <div className="empty-products-state">
                <i className="fas fa-box-open"></i>
                <h3>No matching medical supplies found</h3>
                <p>
                  {searchQuery
                    ? `No results matched "${searchQuery}". Try another keyword or request a custom quote.`
                    : 'No products found in this category right now.'}
                </p>
                {searchQuery && (
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setSearchQuery('')}
                  >
                    Clear Search Filter
                  </button>
                )}
              </div>
            )}

            {!loading &&
              !error &&
              currentProducts.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
          </main>

          {filteredProducts.length > productsPerPage && (
            <Pagination
              productsPerPage={productsPerPage}
              totalProducts={filteredProducts.length}
              paginate={paginate}
              currentPage={currentPage}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductsPage;
